/**
 * KAN-718: форма своего почтового ящика: состояние, пресеты провайдеров,
 * сборка тела запроса и текст результата проверки.
 */
import type {
  Mailbox,
  MailboxBody,
  MailboxCheckResult,
  SmtpSecurity,
} from '@/types/emailMailbox';

/** Состояние формы: порты строками (поле ввода), пароли отдельно. */
export interface MailboxFormState {
  address: string;
  from_name: string;
  smtp_host: string;
  smtp_port: string;
  smtp_security: SmtpSecurity;
  smtp_username: string;
  smtp_password: string;
  imap_enabled: boolean;
  imap_host: string;
  imap_port: string;
  imap_ssl: boolean;
  imap_username: string;
  imap_password: string;
}

export interface MailboxPreset {
  id: string;
  label: string;
  smtp_host: string;
  smtp_port: number;
  smtp_security: SmtpSecurity;
  imap_host: string;
  imap_port: number;
  imap_ssl: boolean;
  hint: string;
}

export const MAILBOX_PRESETS: MailboxPreset[] = [
  {
    id: 'yandex',
    label: 'Яндекс',
    smtp_host: 'smtp.yandex.ru',
    smtp_port: 465,
    smtp_security: 'ssl',
    imap_host: 'imap.yandex.ru',
    imap_port: 993,
    imap_ssl: true,
    hint: 'В настройках Яндекс Почты включите доступ по IMAP и создайте пароль приложения.',
  },
  {
    id: 'mailru',
    label: 'Mail.ru',
    smtp_host: 'smtp.mail.ru',
    smtp_port: 465,
    smtp_security: 'ssl',
    imap_host: 'imap.mail.ru',
    imap_port: 993,
    imap_ssl: true,
    hint: 'Для внешних программ Mail.ru требует пароль приложения.',
  },
  {
    id: 'gmail',
    label: 'Gmail',
    smtp_host: 'smtp.gmail.com',
    smtp_port: 587,
    smtp_security: 'starttls',
    imap_host: 'imap.gmail.com',
    imap_port: 993,
    imap_ssl: true,
    hint: 'Нужен пароль приложения (двухэтапная аутентификация в аккаунте Google).',
  },
  {
    id: 'beget',
    label: 'Beget',
    smtp_host: 'smtp.beget.com',
    smtp_port: 2525,
    smtp_security: 'starttls',
    imap_host: 'imap.beget.com',
    imap_port: 993,
    imap_ssl: true,
    hint: 'Логин: полный адрес ящика, пароль: пароль ящика из панели Beget.',
  },
];

export const SMTP_SECURITY_OPTIONS: Array<{ value: SmtpSecurity; label: string }> = [
  { value: 'ssl', label: 'SSL/TLS' },
  { value: 'starttls', label: 'STARTTLS' },
  { value: 'none', label: 'Без шифрования' },
];

export const EMPTY_MAILBOX_FORM: MailboxFormState = {
  address: '',
  from_name: '',
  smtp_host: '',
  smtp_port: '',
  smtp_security: 'ssl',
  smtp_username: '',
  smtp_password: '',
  imap_enabled: false,
  imap_host: '',
  imap_port: '',
  imap_ssl: true,
  imap_username: '',
  imap_password: '',
};

const portText = (port: number | null | undefined): string => (port ? String(port) : '');

/** Форма из ответа GET; общий ящик (configured=false) даёт пустую форму. */
export function formFromMailbox(mailbox: Mailbox | undefined): MailboxFormState {
  if (!mailbox || !mailbox.configured) return { ...EMPTY_MAILBOX_FORM };
  return {
    address: mailbox.address ?? '',
    from_name: mailbox.from_name ?? '',
    smtp_host: mailbox.smtp_host ?? '',
    smtp_port: portText(mailbox.smtp_port),
    smtp_security: mailbox.smtp_security ?? 'ssl',
    smtp_username: mailbox.smtp_username ?? '',
    smtp_password: '',
    imap_enabled: !!mailbox.imap_enabled,
    imap_host: mailbox.imap_host ?? '',
    imap_port: portText(mailbox.imap_port),
    imap_ssl: mailbox.imap_ssl ?? true,
    imap_username: mailbox.imap_username ?? '',
    imap_password: '',
  };
}

/** Пресет заполняет хосты, порты и шифрование; логин по умолчанию = адрес. */
export function applyPreset(form: MailboxFormState, preset: MailboxPreset): MailboxFormState {
  return {
    ...form,
    smtp_host: preset.smtp_host,
    smtp_port: String(preset.smtp_port),
    smtp_security: preset.smtp_security,
    smtp_username: form.smtp_username || form.address,
    imap_host: preset.imap_host,
    imap_port: String(preset.imap_port),
    imap_ssl: preset.imap_ssl,
    imap_username: form.imap_username || form.address,
  };
}

const parsePort = (value: string): number | null => {
  const n = Number.parseInt(value.trim(), 10);
  return Number.isFinite(n) && n > 0 ? n : null;
};

/** Тело PUT/check: пустые пароли не отправляются (сервер оставит сохранённые). */
export function mailboxBody(form: MailboxFormState): MailboxBody {
  const body: MailboxBody = {
    address: form.address.trim(),
    from_name: form.from_name.trim(),
    smtp_host: form.smtp_host.trim(),
    smtp_port: parsePort(form.smtp_port),
    smtp_security: form.smtp_security,
    smtp_username: form.smtp_username.trim(),
    imap_enabled: form.imap_enabled,
    imap_host: form.imap_host.trim(),
    imap_port: parsePort(form.imap_port),
    imap_ssl: form.imap_ssl,
    imap_username: form.imap_username.trim(),
  };
  if (form.smtp_password) body.smtp_password = form.smtp_password;
  if (form.imap_password) body.imap_password = form.imap_password;
  return body;
}

/** Обязательные поля для сохранения; пустой список = можно сохранять. */
export function missingMailboxFields(form: MailboxFormState): string[] {
  const missing: string[] = [];
  if (!form.address.trim()) missing.push('адрес');
  if (!form.smtp_host.trim()) missing.push('SMTP-сервер');
  if (!parsePort(form.smtp_port)) missing.push('SMTP-порт');
  if (form.imap_enabled) {
    if (!form.imap_host.trim()) missing.push('IMAP-сервер');
    if (!parsePort(form.imap_port)) missing.push('IMAP-порт');
  }
  return missing;
}

/** "SMTP: работает, IMAP: ошибка" плюс текст ошибки. */
export function mailboxCheckSummary(result: MailboxCheckResult): string {
  const parts = [`SMTP: ${result.smtp_ok ? 'работает' : 'ошибка'}`];
  if (result.imap_ok !== null && result.imap_ok !== undefined) {
    parts.push(`IMAP: ${result.imap_ok ? 'работает' : 'ошибка'}`);
  }
  const head = parts.join(', ');
  return result.error ? `${head}. ${result.error}` : head;
}

export function mailboxCheckOk(result: MailboxCheckResult): boolean {
  return result.smtp_ok && result.imap_ok !== false;
}
